import { Link, useLoaderData } from 'react-router';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';

interface Batch {
  id: number;
  batchCode: string;
  status: string;
  emailSentAt: string | null;
  excelFilename: string | null;
}

interface LoaderData {
  batches: Batch[];
}

export async function batchesLoader(): Promise<LoaderData> {
  return api.get<LoaderData>('/fulfillment/batches');
}

export function BatchesPage() {
  const { batches } = useLoaderData() as LoaderData;
  return (
    <div>
      <header>
        <p className="label-overline">기록</p>
        <h1 className="mt-1 text-[28px] font-light tracking-[-0.01em] text-ink-950">발주 배치</h1>
      </header>

      {/* Mobile cards */}
      <div className="mt-4 space-y-2.5 sm:hidden">
        {batches.length === 0 ? (
          <div className="rounded-lg border border-ink-200 bg-paper px-4 py-10 text-center text-sm text-ink-500">
            배치가 없습니다.
          </div>
        ) : (
          batches.map((b) => (
            <Link
              key={b.id}
              to={`/batches/${b.batchCode}`}
              className="block rounded-xl border border-ink-200 bg-paper p-4 shadow-sm active:bg-ink-50"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="break-all font-mono text-base font-semibold text-brand-700">{b.batchCode}</span>
                <span className="shrink-0 rounded-md border border-ink-200 bg-ink-50 px-2 py-0.5 text-xs font-medium text-ink-700">
                  {b.status}
                </span>
              </div>
              <dl className="mt-3 space-y-2">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-400">엑셀</dt>
                  <dd className="mt-0.5 break-all text-sm text-ink-700">{b.excelFilename ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-400">발송일시</dt>
                  <dd className="mt-0.5 text-sm text-ink-700">{b.emailSentAt ? formatDateTime(b.emailSentAt) : '—'}</dd>
                </div>
              </dl>
            </Link>
          ))
        )}
      </div>

      {/* Desktop table */}
      <div className="mt-4 hidden overflow-hidden rounded-lg border border-ink-200 bg-paper sm:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-ink-50 text-ink-600">
              <tr>
                <th className="px-3 py-2 text-left">배치 코드</th>
                <th className="px-3 py-2 text-left">상태</th>
                <th className="px-3 py-2 text-left">엑셀</th>
                <th className="px-3 py-2 text-left">발송일시</th>
              </tr>
            </thead>
            <tbody>
              {batches.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-ink-500">
                    배치가 없습니다.
                  </td>
                </tr>
              )}
              {batches.map((b) => (
                <tr key={b.id} className="border-t border-ink-100 hover:bg-ink-50">
                  <td className="px-3 py-2 font-mono">
                    <Link to={`/batches/${b.batchCode}`} className="text-brand-700 hover:underline">
                      {b.batchCode}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{b.status}</td>
                  <td className="px-3 py-2 text-xs text-ink-600">{b.excelFilename ?? '—'}</td>
                  <td className="px-3 py-2 text-xs text-ink-600">
                    {b.emailSentAt ? formatDateTime(b.emailSentAt) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
